import { useState } from "react";
import { Rate } from "antd";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { createSearchParams, useLocation, useNavigate, useParams } from "react-router-dom";
import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import { getAvatarLink } from "@base/helpers/common";
import useCheckLogin from "@base/hooks/useCheckLogin";
import useToggle from "@base/hooks/useToggle";
import { formatDoctorPrice, getActiveBranches, getStarCount } from "@base/modules/doctor/doctorProfile";
import useGetDoctorBySlug from "@base/modules/doctor/hooks/useGetDoctorBySlug";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import { ISiteBranch } from "@base/modules/site/site.interface";
import { baseRoutes } from "@base/routes/baseRoutes";
import themeColor from "@base/themes/colorTheme";
import { responseUI } from "@base/themes/response";
import CommonStyles from "@components/CommonStyles";
import BranchPickerModal from "@components/Doctor/[id]/BranchPickerModal";
import DoctorFacts from "@components/Doctor/[id]/DoctorFacts";
import DoctorProfileSections from "@components/Doctor/[id]/DoctorProfileSections";
import DoctorRatings from "@components/Doctor/[id]/DoctorRatings";

type TBookingType = "online" | "offline";

export default function DoctorDetail() {
  //! state
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLogin } = useCheckLogin(false);
  const { data: doctor, isLoading, error, refetch } = useGetDoctorBySlug(String(slug), i18n.resolvedLanguage);
  const { open: openBranches, shouldRender: shouldRenderBranches, toggle: toggleBranches } = useToggle();
  const [pendingType, setPendingType] = useState<TBookingType>("online");

  //! function
  const goToBooking = (type: TBookingType, branch?: ISiteBranch) => {
    if (!doctor) {
      return;
    }

    const specializationCode = doctor.specialization?.specialization_code;

    navigate({
      pathname: baseRoutes.bookingAppointment,
      search: createSearchParams({
        doctor_code: String(doctor.doctor_code),
        site_code: String(doctor.site_code),
        type,
        ...(specializationCode ? { specialization_code: specializationCode } : {}),
        ...(branch?.code ? { site_branch_code: branch.code, site_branch_name: branch.name ?? "" } : {}),
      }).toString(),
    });
  };

  const handleBooking = (type: TBookingType) => {
    // Back to this profile after signing in, so the branch can still be picked here.
    if (!isLogin) {
      navigate({
        pathname: baseRoutes.login,
        search: createSearchParams({ redirectTo: `${location.pathname}${location.search}` }).toString(),
      });
      return;
    }

    if (getActiveBranches(doctor).length) {
      setPendingType(type);
      toggleBranches();
      return;
    }

    goToBooking(type);
  };

  //! render
  if (isLoading) {
    return <CommonStyles.LoadingUI />;
  }

  if (error || !doctor) {
    const isNotFound = (error as AxiosError)?.response?.status === 404;

    return (
      <div className="bg-common-background">
        <div className={`${responseUI.screen} ${responseUI.padding} py-16 flex flex-col items-center gap-4 text-center`}>
          <CommonIcons.IconNotFound />
          <span className="text-base font-medium font-['Inter'] text-common-maintext">
            {isNotFound ? t("doctorDetail.notFound") : t("doctorDetail.loadFailed")}
          </span>
          {!isNotFound ? (
            <CommonStyles.ButtonUI className="h-10 px-6 font-medium" onClick={refetch}>
              {t("Retry")}
            </CommonStyles.ButtonUI>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-common-white">
      <div className={`${responseUI.screen} ${responseUI.padding} relative py-6 lg:py-10`}>
        {/* Decoration, anchored to the content column rather than the viewport edge. */}
        <div className="absolute right-0 bottom-0 max-xl:hidden pointer-events-none">
          <CommonIcons.IconHeart width={150} height={150} />
        </div>

        <div className="relative z-[1]">
          <DoctorSummary doctor={doctor} onBook={handleBooking} />
        </div>
      </div>

      <div className="bg-common-background">
        <div className={`${responseUI.screen} ${responseUI.padding} py-6 lg:py-8 flex flex-col gap-4 lg:gap-6`}>
          <DoctorProfileSections doctor={doctor} />
          <DoctorRatings doctorCode={doctor.doctor_code} enabled={isLogin} />
        </div>
      </div>

      {shouldRenderBranches && (
        <BranchPickerModal
          open={openBranches}
          toggle={toggleBranches}
          branches={getActiveBranches(doctor)}
          onSelect={(branch) => {
            toggleBranches();
            goToBooking(pendingType, branch);
          }}
        />
      )}
    </div>
  );
}

interface IDoctorSummaryProps {
  doctor: IDoctor;
  onBook: (type: TBookingType) => void;
}

/** Photo, name, rating, the three facts and the ways to book. */
function DoctorSummary({ doctor, onBook }: IDoctorSummaryProps) {
  //! state
  const { t } = useTranslation();
  const avatar = getAvatarLink(doctor.avatar ?? []) ?? doctor.avatar?.[0]?.link;
  // A dead link shows the default picture, and then there is nothing to enlarge.
  const [hasAvatarFailed, setHasAvatarFailed] = useState<boolean>(false);
  const ratingCount = doctor.ratings ?? 0;

  const bookingOptions = [
    {
      type: "online" as const,
      visible: doctor.online_dr === true,
      label: t("doctorDetail.onlineConsultation"),
      price: formatDoctorPrice(doctor.price_online),
      icon: <CommonIcons.IconCameraBox />,
    },
    {
      type: "offline" as const,
      visible: doctor.offline_dr === true,
      label: t("doctorDetail.atFacility"),
      price: formatDoctorPrice(doctor.price),
      icon: <CommonIcons.IconFacilityBox />,
    },
  ].filter((option) => option.visible);

  //! render
  return (
    <div className="flex flex-col md:flex-row gap-6 lg:gap-10">
      <div className="flex-none self-center md:self-start w-[180px] lg:w-[240px] p-2 lg:p-3 rounded-2xl bg-common-background">
        {/* The full picture opens on click; a doctor without one has nothing to open. */}
        <CommonStyles.ImageUI
          className="rounded-xl aspect-square object-cover"
          src={avatar}
          fallback={CommonImages.DefaultDoctorAvatarSquare}
          preview={avatar && !hasAvatarFailed ? { src: avatar } : false}
          onError={() => setHasAvatarFailed(true)}
        />
      </div>

      <div className="grow min-w-0 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-xl lg:text-2xl font-semibold font-['Inter'] text-common-maintext break-words">{doctor.doctor_name}</h1>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Rate disabled value={getStarCount(doctor.rating_point)} style={{ fontSize: 16, color: themeColor.yellow500 }} />
            <span className="text-sm font-['Inter'] text-common-subtext">({t("doctorDetail.ratingCount", { count: ratingCount })})</span>
          </div>
        </div>

        <div className="pt-4 border-0 border-t border-solid border-common-divider">
          <DoctorFacts doctor={doctor} />
        </div>

        {bookingOptions.length ? (
          <div className="grid sm:grid-cols-2 gap-3 max-w-[640px] pt-2">
            {bookingOptions.map((option) => (
              <button
                key={option.type}
                type="button"
                onClick={() => onBook(option.type)}
                className="flex items-center gap-3 p-3 pr-4 rounded-xl border border-solid border-common-primary200 bg-common-primary50 text-left cursor-pointer hover:border-common-primary hover:bg-common-primary100 transition-colors"
              >
                <span className="flex-none">{option.icon}</span>
                <span className="grow min-w-0 flex flex-col">
                  <span className="text-sm lg:text-base font-medium font-['Inter'] text-common-maintext">{option.label}</span>
                  <span className="text-sm lg:text-base font-semibold font-['Inter'] text-common-primary">
                    {option.price ?? t("doctorDetail.bookNow")}
                  </span>
                </span>
                <CommonIcons.RightOutlined className="flex-none text-common-primary" />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
